import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Toaster, toast } from 'react-hot-toast';
import './assets/css/PeIcon.scss';
import './App.scss';
import "react-datepicker/dist/react-datepicker.css";

// Context imports
import { SocketProvider } from './contexts/SocketContext';

// FCM Push Notification hook
import useFCM from './hooks/useFCM';

// Component imports
import Sidebar from './components/Sidebar';
import CustomNavbar from './components/Navbar';
import SubMenu from './components/SubMenu';
import BreadCrumb from './components/BreadCrumb';
import { PageLoader } from './components/LoadingComponents';
import LazyWrapper from './components/LazyWrapper';
import NotFound from './components/NotFound';
import SubscriptionBanner from './components/SubscriptionBanner';

// Import lazy components
import {
  Dashboard,
  SignIn,
  Academic,
  HolidayManager,
  DashboardOverview,
  EnhancedClassSections,
  SectionManagement,
  SubjectManagement,
  StudentDashboard,
  StudentManagement,
  StudentGroupManagement,
  StudentAttendance,
  SiblingManagement,
  StudentPromotion,
  FeeDashboard,
  FeeAnalytics,
  FeeCategoryManagement,
  FeeStructureManagement,
  StudentFeeManagement,
  FeePaymentManagement,
  ExamManagement,
  OnlineExamManagement,
  StudentExamRoutes,
  AssignmentManagement,
  Teachers,
  Employees,
  EmployeeAttendance,
  SalaryComponents,
  SalaryStructures,
  EmployeeSalary,
  PayrollRuns,
  PayrollRunDetail,
  StatutoryConfig,
  LeaveTypes,
  LeaveRequests,
  LeaveCalendar,
  Loans,
  Reimbursements,
  Documents,
  Form16,
  TaxDeclarations,
  Tracking,
  RoleManagement,
  MenuManagement,
  FeedbackManagement,
  FeedbackListPage,
  FeedbackStatsPage,
  TemplateBuilder,
  SchoolCalendar,
  ChatInterface,
  TransferCertificateIssue,
  TransferCertificateList,
  TransferCertificatePreview,
  StoreDashboard,
  StoreItems,
  StoreCategories,
  StoreSuppliers,
  StoreRequisitions,
  StorePurchaseOrders,
  StoreGoodsReceipts,
  StoreStockLedger,
  StoreAdjustments,
  StoreIssues,
  StoreAlerts,
  StoreReports,
  StoreTransfers,
  StoreUnits,
  LibraryDashboard,
  LibraryCatalog,
  LibraryMembers,
  LibraryCirculation,
  LibraryRenewals,
  LibraryReservations,
  LibraryFines,
  LibraryReports,
  LibrarySettings,
  TransportDashboard,
  TransportVehicles,
  TransportRoutes,
  TransportStudents,
  TransportAttendance,
  TransportMaintenance,
  TransportReports,
  TransportSettings,
  TransportDrivers,
  SubscriptionPage,
} from './components/LazyComponents';

// Toast configuration
const TOAST_STYLES = {
  fullscreenActivated: {
    icon: '🔍',
    duration: 4000,
    style: {
      background: 'linear-gradient(135deg, #0948b3 0%, #1e5cc7 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 8px 25px rgba(9, 72, 179, 0.3)',
    },
    position: 'top-center',
  },
  fullscreenDeactivated: {
    icon: '✓',
    duration: 2000,
    style: {
      background: 'linear-gradient(135deg, #28a745 0%, #34ce57 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 6px 20px rgba(40, 167, 69, 0.3)',
    },
    position: 'top-center',
  }
};

// Fullscreen hook — uses native browser Fullscreen API only
const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const handleFullscreen = React.useCallback(() => {
    const doc = document.documentElement;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const requestFs = doc.requestFullscreen || doc.webkitRequestFullscreen;
      if (requestFs) requestFs.call(doc).catch(() => {});
    } else {
      const exitFs = document.exitFullscreen || document.webkitExitFullscreen;
      if (exitFs) exitFs.call(document).catch(() => {});
    }
  }, []);

  // Sync React state with browser fullscreen changes (enter/exit via button, Esc, F11)
  React.useEffect(() => {
    const onFullscreenChange = () => {
      // setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
      setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  return {
    isFullscreen,
    isTransitioning: false,
    handleFullscreen,
  };
};

// AuthRouter component
const AuthRouter = () => (
  <div className="auth-container">
    <LazyWrapper fallback={<PageLoader text="Loading authentication..." /> }
 >
      <Routes>
        <Route path="/" element={<SignIn />} />
        <Route path="signin" element={<SignIn />} />
        <Route path="/template-builder" element={<TemplateBuilder />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LazyWrapper>
  </div>
);

// Application Routes component
const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Dashboard />} />
    <Route path="/dashboard" element={<Dashboard />} />

    <Route path="/class-and-sections/analytics" element={<DashboardOverview />} />
    <Route path="/class-and-sections/manage-classes" element={<EnhancedClassSections />} />
    <Route path="/class-and-sections/manage-sections" element={<SectionManagement />} />
    <Route path="/class-and-sections/manage-subjects" element={<SubjectManagement />} />

    <Route path="/students/analytics" element={<StudentDashboard />} />
    <Route path="/students/manage" element={<StudentManagement />} />
    <Route path="/students/groups" element={<StudentGroupManagement />} />
    <Route path="/students/attendance" element={<StudentAttendance />} />
    <Route path="/students/siblings-management" element={<SiblingManagement />} />
    <Route path="/students/promotion" element={<StudentPromotion />} />

    <Route path="/student-fees/analytics-board" element={<FeeDashboard />} />
    <Route path="/student-fees/fee-analytics" element={<FeeAnalytics />} />
    <Route path="/student-fees/categories" element={<FeeCategoryManagement />} />
    <Route path="/student-fees/structures" element={<FeeStructureManagement />} />
    <Route path="/student-fees/student-fees" element={<StudentFeeManagement />} />
    <Route path="/student-fees/payments" element={<FeePaymentManagement />} />

    <Route path="/exams/offline-exam" element={<ExamManagement />} />
    <Route path="/exams/online-exam" element={<OnlineExamManagement />} />
    <Route path="/exams/student-exams/*" element={<StudentExamRoutes />} />

    <Route path="/assignments/manage" element={<AssignmentManagement />} />

    <Route path="/teachers/manage" element={<Teachers />} />

    <Route path="/hr-payroll/employees" element={<Employees />} />
    <Route path="/hr-payroll/employee-attendance" element={<EmployeeAttendance />} />
    <Route path="/hr-payroll/salary-components" element={<SalaryComponents />} />
    <Route path="/hr-payroll/salary-structures" element={<SalaryStructures />} />
    <Route path="/transfer-certificates" element={<Navigate to="/transfer-certificates/list" replace />} />
    <Route path="/transfer-certificates/issue" element={<TransferCertificateIssue />} />
    <Route path="/transfer-certificates/list" element={<TransferCertificateList />} />
    <Route path="/transfer-certificates/preview/:studentId" element={<TransferCertificatePreview />} />
    <Route path="/hr-payroll/employee-salary" element={<EmployeeSalary />} />
    <Route path="/hr-payroll/payroll-runs" element={<PayrollRuns />} />
    <Route path="/hr-payroll/payroll-runs/:id" element={<PayrollRunDetail />} />
    <Route path="/hr-payroll/statutory-config" element={<StatutoryConfig />} />
    <Route path="/hr-payroll/leave-types" element={<LeaveTypes />} />
    <Route path="/hr-payroll/leave-requests" element={<LeaveRequests />} />
    <Route path="/hr-payroll/leave-calendar" element={<LeaveCalendar />} />
          <Route path="/hr-payroll/loans" element={<Loans />} />
          <Route path="/hr-payroll/reimbursements" element={<Reimbursements />} />
          <Route path="/hr-payroll/documents" element={<Documents />} />
          <Route path="/hr-payroll/form16" element={<Form16 />} />
          <Route path="/hr-payroll/tax-declarations" element={<TaxDeclarations />} />

    <Route path="/settings/academic-settings" element={<Academic />} />
    <Route path="/settings/holiday-manager" element={<HolidayManager />} />
    <Route path="/settings/manage-roles" element={<RoleManagement />} />
    <Route path="/settings/menu-management" element={<MenuManagement />} />

    <Route path="/feedback/manage" element={<FeedbackManagement />} />
    <Route path="/feedback/list" element={<FeedbackListPage />} />
    <Route path="/feedback/stats" element={<FeedbackStatsPage />} />

    <Route path="/transport/vehicle-tracking" element={<Tracking />} />
    <Route path="/template-builder" element={<TemplateBuilder />} />
    <Route path="/calendar" element={<SchoolCalendar />} />

    {/* Chat and Messages Routes */}
    <Route path="/communication/chat" element={<ChatInterface />} />

    {/* Store Management Routes */}
    <Route path="/store-management" element={<Navigate to="/store-management/dashboard" replace />} />
    <Route path="/store-management/dashboard" element={<StoreDashboard />} />
    <Route path="/store-management/items" element={<StoreItems />} />
    <Route path="/store-management/categories" element={<StoreCategories />} />
    <Route path="/store-management/units" element={<StoreUnits />} />
    <Route path="/store-management/suppliers" element={<StoreSuppliers />} />
    <Route path="/store-management/requisitions" element={<StoreRequisitions />} />
    <Route path="/store-management/purchase-orders" element={<StorePurchaseOrders />} />
    <Route path="/store-management/grn" element={<StoreGoodsReceipts />} />
    <Route path="/store-management/stock-ledger" element={<StoreStockLedger />} />
    <Route path="/store-management/adjustments" element={<StoreAdjustments />} />
    <Route path="/store-management/issues" element={<StoreIssues />} />
    <Route path="/store-management/alerts" element={<StoreAlerts />} />
    <Route path="/store-management/reports" element={<StoreReports />} />
    <Route path="/store-management/transfers" element={<StoreTransfers />} />

    {/* Library Management Routes */}
    <Route path="/library" element={<Navigate to="/library/dashboard" replace />} />
    <Route path="/library/dashboard"    element={<LibraryDashboard />} />
    <Route path="/library/catalog"      element={<LibraryCatalog />} />
    <Route path="/library/members"      element={<LibraryMembers />} />
    <Route path="/library/circulation"  element={<LibraryCirculation />} />
    <Route path="/library/renewals"     element={<LibraryRenewals />} />
    <Route path="/library/reservations" element={<LibraryReservations />} />
    <Route path="/library/fines"        element={<LibraryFines />} />
    <Route path="/library/reports"      element={<LibraryReports />} />
    <Route path="/library/settings"     element={<LibrarySettings />} />

    {/* Transport Management Routes */}
    <Route path="/transport" element={<Navigate to="/transport/dashboard" replace />} />
    <Route path="/transport/dashboard"    element={<TransportDashboard />} />
    <Route path="/transport/vehicles"     element={<TransportVehicles />} />
    <Route path="/transport/routes"       element={<TransportRoutes />} />
    <Route path="/transport/students"     element={<TransportStudents />} />
    <Route path="/transport/attendance"   element={<TransportAttendance />} />
    <Route path="/transport/maintenance"  element={<TransportMaintenance />} />
    <Route path="/transport/reports"      element={<TransportReports />} />
    <Route path="/transport/settings"     element={<TransportSettings />} />
    <Route path="/transport/drivers"      element={<TransportDrivers />} />

    <Route path="/subscription" element={<SubscriptionPage />} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);

// Toaster configuration component
const AppToaster = ({ isFullscreen }) => (
  <Toaster
    position="top-center"
    containerAriaProps={{
      'aria-live': 'polite',
      'aria-atomic': 'true',
      role: 'status',
      'aria-label': 'Notifications',
    }}
    toastOptions={{
      duration: 4000,
      style: {
        background: 'var(--white)',
        color: 'var(--text-primary)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--gray-200)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        padding: '12px 16px',
      },
      success: {
        iconTheme: {
          primary: 'var(--success)',
          secondary: 'var(--white)',
        },
      },
      error: {
        iconTheme: {
          primary: 'var(--danger)',
          secondary: 'var(--white)',
        },
      },
      className: 'custom-toast',
    }}
    containerStyle={{
      top: isFullscreen ? '80px' : '20px',
      zIndex: isFullscreen ? 10002 : 9999,
    }}
  />
);

// Main Dashboard Router component
const DashboardRouter = () => {
  const { sidebar } = useSelector(state => state.ui);
  const { current_academic_id } = useSelector(state => state.auth);
  const { isFullscreen, isTransitioning, handleFullscreen } = useFullscreen();

  // Initialize FCM push notifications (registers token, listens for foreground messages)
  useFCM();

  return (
    <div className={`app-container${isFullscreen ? ' fullscreen' : ''}${isTransitioning ? ' transitioning' : ''}`}>
      <AppToaster isFullscreen={isFullscreen} />

      {/* Navigation Components - Hidden in fullscreen */}
      {!isFullscreen && (
        <>
          <CustomNavbar onFullscreen={handleFullscreen} />
          <Sidebar />
        </>
      )}

      <div className={`main-content sidebar-${sidebar} ${isFullscreen ? 'fullscreen-main' : ''}`}>
        {/* SubMenu - Hidden in fullscreen */}
        {!isFullscreen && <SubMenu />}

        <div className={`content ${isFullscreen ? 'fullscreen-content' : ''}`}>
          {/* Subscription expiry banner - shown before breadcrumb */}
          {!isFullscreen && <SubscriptionBanner />}
          {/* BreadCrumb - Hidden in fullscreen */}
          {!isFullscreen && <BreadCrumb />}
          
          <div className={`page-content ${isFullscreen ? 'fullscreen-page-content' : ''}`}>
            <LazyWrapper 
              key={current_academic_id} 
              fallback={<PageLoader text="Loading page..." />}
            >
              <AppRoutes />
            </LazyWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component - Controls authentication flow
const App = () => {
  const { token, _persist} = useSelector(state => state.auth);
  const [isLoading, setIsLoading] = React.useState(true);

  // Check if redux-persist has rehydrated the store
  const isRehydrated = _persist && _persist.rehydrated;

  useEffect(() => {
    // Only set loading to false after persistence is rehydrated
    if (isRehydrated) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000); // Reduced loading time for better UX
      return () => clearTimeout(timer);
    }
  }, [isRehydrated]);

  // Show loading while waiting for persistence rehydration or app initialization
  if (!isRehydrated || isLoading) {
    return <PageLoader text="Initializing Proton..." />;
  }

  return (
    <Router>
      {token ? (
        <SocketProvider>
          <DashboardRouter />
        </SocketProvider>
      ) : (
        <AuthRouter />
      )}
    </Router>
  );
};

export default React.memo(App);
